// src/components/ui/molecules/NutritionFacts.tsx
// @ts-nocheck
import React from "react";

export interface NutritionData {
  calories: number;
  fat: number;
  carbs: number;
  protein: number;
  sugar: number;
  fiber: number;
  fatBreakdown: {
    trans: number;
    saturated: number;
    monounsaturated: number;
    polyunsaturated: number;
  };
  micronutrients: Record<string, number>;
}

interface NutrientInfo {
  label: string;
  dv?: number;
  unit: string; // e.g. "mg", "µg", "g", "kcal"
}

const NUTRIENT_INFO: Record<string, NutrientInfo> = {
  calories:         { label: "Calories",            dv: 2000, unit: "kcal" },
  fat:              { label: "Total Fat",            dv:   78, unit: "g"    },
  "fatBreakdown.saturated":       { label: "Saturated Fat",         dv:   20, unit: "g" },
  "fatBreakdown.trans":           { label: "Trans Fat",             dv:    2, unit: "g" },
  "fatBreakdown.monounsaturated": { label: "Monounsaturated Fat",   unit: "g"    },
  "fatBreakdown.polyunsaturated": { label: "Polyunsaturated Fat",   unit: "g"    },

  carbs:            { label: "Carbohydrates",        dv:  275, unit: "g"    },
  "carbs.fiber":    { label: "Dietary Fiber",        dv:   28, unit: "g"    },
  "carbs.sugar":    { label: "Total Sugar",          dv:   50, unit: "g"    },

  protein:          { label: "Protein",              dv:   50, unit: "g"    },

  iron_mg:          { label: "Iron",                 dv:   18, unit: "mg"   },
  calcium_mg:       { label: "Calcium",              dv: 1300, unit: "mg"   },
  sodium_mg:        { label: "Sodium",               dv: 2300, unit: "mg"   },
  potassium_mg:     { label: "Potassium",            dv: 4700, unit: "mg"   },
  vitaminC_mg:      { label: "Vitamin C",            dv:   90, unit: "mg"   },
  vitaminA_mcg:     { label: "Vitamin A",            dv:  900, unit: "µg"   },
  vitaminE_mg:      { label: "Vitamin E",            dv:   15, unit: "mg"   },
  vitaminK_mcg:     { label: "Vitamin K",            dv:  120, unit: "µg"   },
  magnesium_mg:     { label: "Magnesium",            dv:  420, unit: "mg"   },
  phosphorus_mg:    { label: "Phosphorus",           dv: 1250, unit: "mg"   },
  zinc_mg:          { label: "Zinc",                 dv:   11, unit: "mg"   },
};

interface Props {
  data: NutritionData;
  originalServings: number;
  selectedServings: number;
}

const NutritionFacts: React.FC<Props> = ({
  data,
  originalServings,
  selectedServings,
}) => {
  const scale = selectedServings / originalServings;
  // one‐decimal rounding
  const fmt = (n: number) => Math.round(n * 10) / 10;

  // build row list
  const rows: { key: string; value: number }[] = [];

  // macros + breakdown
  rows.push({ key: "calories", value: data.calories });
  rows.push({ key: "fat",      value: data.fat      });
  rows.push({ key: "fatBreakdown.saturated",      value: data.fatBreakdown.saturated });
  rows.push({ key: "fatBreakdown.trans",          value: data.fatBreakdown.trans });
  rows.push({ key: "fatBreakdown.monounsaturated",value: data.fatBreakdown.monounsaturated });
  rows.push({ key: "fatBreakdown.polyunsaturated",value: data.fatBreakdown.polyunsaturated });

  // carbs + breakdown
  rows.push({ key: "carbs",          value: data.carbs });
  rows.push({ key: "carbs.fiber",    value: data.fiber });
  rows.push({ key: "carbs.sugar",    value: data.sugar });

  // protein
  rows.push({ key: "protein",value: data.protein });

  // micronutrients (only non-zero AND with a rendering entry)
  for (const [k, v] of Object.entries(data.micronutrients)) {
    if (v === 0) continue;
    if (!NUTRIENT_INFO[k]) continue;      // ← drop any nutrient you won't render
    rows.push({ key: k, value: v });
  }

  // helper to handle large mg/µg values
  function formatWithUnit(raw: number, unit: string) {
    let amount = raw;
    let displayUnit = unit;

    if (unit === "mg" && amount >= 1000) {
      amount = amount / 1000;
      displayUnit = "g";
    } else if (unit === "µg" && amount >= 1000) {
      amount = amount / 1000;
      displayUnit = "mg";
    }

    return { amount: fmt(amount), unit: displayUnit };
  }

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2">Nutrient</th>
          <th className="text-right py-2">Amount</th>
          <th className="text-right py-2">% Daily Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const info = NUTRIENT_INFO[row.key];
          if (!info) return null;

          // 1) scaled (in the same base unit as info.dv):
          const rawScaled = row.value * scale;

          // 2) compute %DV using rawScaled in base units:
          const pct = info.dv != null
            ? Math.round((rawScaled / info.dv) * 100)
            : undefined;

          // 3) then turn rawScaled into a human unit for display:
          const { amount, unit } = formatWithUnit(rawScaled, info.unit);

          const indent = row.key.includes(".") ? "pl-6" : "";

          return (
            <tr key={row.key} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className={`py-1 ${indent}`}>{info.label}</td>
              <td className="py-1 text-right">
                {amount} {unit}
              </td>
              <td className="py-1 text-right">
                {pct != null ? `${pct}%` : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default NutritionFacts;
